import type { ApiMenuItem } from "@/features/theme/types/brandTheme.type";

const normalizeRoute = (route: string) => (route.length > 1 ? route.replace(/\/+$/, "") : route);

const isLegacy = (item: ApiMenuItem) => item.key.endsWith("_legacy");

// Hidden items mount drawers and groups own children: only visible leaves can collide.
const isDedupableLeaf = (item: ApiMenuItem) => !item.hidden && !item.children?.length && !!item.route;

const pickKeptKeys = (items: ApiMenuItem[]): Set<string> => {
  const keptByRoute = new Map<string, ApiMenuItem>();
  const visit = (list: ApiMenuItem[]) => list.forEach((item) => {
    if (item.children?.length) visit(item.children);
    if (!isDedupableLeaf(item)) return;

    const route = normalizeRoute(item.route as string);
    const kept = keptByRoute.get(route);
    if (!kept || (isLegacy(kept) && !isLegacy(item))) keptByRoute.set(route, item);
  });
  visit(items);
  return new Set([...keptByRoute.values()].map((item) => item.key));
};

/**
 * Keeps a single entry per route. The API can send two leaves pointing to the
 * same screen (e.g. `base_documentaire` and `base_documentaire_legacy` when
 * both drive flags are on, YTI-2008): the first one wins, a non-legacy entry
 * wins over a legacy one, and a group left without children is dropped unless it has its own route.
 */
export const dedupeMenuItems = (items: ApiMenuItem[]): ApiMenuItem[] => {
  const keptKeys = pickKeptKeys(items);

  const filterLevel = (list: ApiMenuItem[]): ApiMenuItem[] => list.flatMap((item): ApiMenuItem[] => {
    if (item.children?.length) {
      const children = filterLevel(item.children);
      // Same rule as the API: a group with its own route stays as a link.
      if (!children.length && !item.route) return [];
      return [{ ...item, children }];
    }
    if (isDedupableLeaf(item) && !keptKeys.has(item.key)) return [];
    return [item];
  });

  return filterLevel(items);
};
